import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Table,
  Spin,
  message,
  Statistic,
  Tabs,
  Tag,
  Typography
} from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  DollarOutlined,
  TransactionOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
  WalletOutlined,
  BankOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';

// Create axios instance — must be defined before any component that uses it
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/api',
  timeout: 10000,
});
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { Text } = Typography;

// Balance Reports Component
const BalanceReports = ({ loading }) => {
  const [balanceData, setBalanceData] = useState(null);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    fetchBalanceData();
  }, []);

  const fetchBalanceData = async () => {
    try {
      const membersResponse = await adminApi.get('/members', {
        params: { limit: 1000 }
      });
      setMembers(membersResponse.data.members || []);

      // Calculate statistics
      const membersData = membersResponse.data.members || [];
      const stats = {
        total_deposit_balance: membersData.reduce((sum, member) => sum + parseFloat(member.deposit_balance || 0), 0),
        total_available_balance: membersData.reduce((sum, member) => sum + parseFloat(member.available_balance || 0), 0),
        total_pending_balance: membersData.reduce((sum, member) => sum + parseFloat(member.pending_balance || 0), 0),
        total_frozen_balance: membersData.reduce((sum, member) => sum + parseFloat(member.frozen_balance || 0), 0),
      };
      setBalanceData(stats);
    } catch (error) {
      console.error('Fetch balance data error:', error);

      // Use mock data as fallback
      const mockMembers = [
        { member_id: 'MBR000001', full_name: 'Merchant A', deposit_balance: 5000000, available_balance: 3500000, pending_balance: 500000, frozen_balance: 0 },
        { member_id: 'MBR000002', full_name: 'Merchant B', deposit_balance: 8000000, available_balance: 6200000, pending_balance: 800000, frozen_balance: 100000 },
        { member_id: 'MBR000003', full_name: 'Merchant C', deposit_balance: 3500000, available_balance: 2800000, pending_balance: 300000, frozen_balance: 50000 }
      ];

      const mockBalanceData = {
        total_deposit_balance: 16500000,
        total_available_balance: 12500000,
        total_pending_balance: 1600000,
        total_frozen_balance: 150000
      };

      setMembers(mockMembers);
      setBalanceData(mockBalanceData);
      console.log('Using fallback mock data for balance reports');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const balanceColumns = [
    {
      title: 'ID Member',
      dataIndex: 'member_id',
      key: 'member_id',
      width: 120,
    },
    {
      title: 'Nama Merchant',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
    },
    {
      title: 'Deposit Balance',
      dataIndex: 'deposit_balance',
      key: 'deposit_balance',
      width: 150,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Available Balance',
      dataIndex: 'available_balance',
      key: 'available_balance',
      width: 150,
      render: (amount) => (
        <Text style={{ color: '#1890ff' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Pending Balance',
      dataIndex: 'pending_balance',
      key: 'pending_balance',
      width: 150,
      render: (amount) => (
        <Text style={{ color: '#faad14' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Frozen Balance',
      dataIndex: 'frozen_balance',
      key: 'frozen_balance',
      width: 150,
      render: (amount) => (
        <Text style={{ color: '#ff4d4f' }}>
          {formatCurrency(amount)}
        </Text>
      ),
    },
    {
      title: 'Total Balance',
      key: 'total_balance',
      width: 150,
      render: (_, record) => {
        const total = (parseFloat(record.deposit_balance || 0) +
                       parseFloat(record.available_balance || 0) +
                       parseFloat(record.pending_balance || 0) +
                       parseFloat(record.frozen_balance || 0));
        return (
          <Text strong style={{ color: '#722ed1' }}>
            {formatCurrency(total)}
          </Text>
        );
      },
    },
    {
      title: 'KYC Status',
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      width: 100,
      render: (status) => {
        const colorMap = {
          verified: 'green',
          unverified: 'orange',
          rejected: 'red'
        };
        return (
          <Tag color={colorMap[status] || 'default'}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
  ];

  return (
    <div>
      {/* Balance Summary Cards */}
      {balanceData && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Deposit Balance"
                value={balanceData.total_deposit_balance}
                prefix={<WalletOutlined />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Available Balance"
                value={balanceData.total_available_balance}
                prefix={<BankOutlined />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Pending Balance"
                value={balanceData.total_pending_balance}
                prefix={<HistoryOutlined />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total All Balances"
                value={balanceData.total_deposit_balance + balanceData.total_available_balance + balanceData.total_pending_balance}
                prefix={<DollarOutlined />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Balance Details Table */}
      <Card
        title="Detail Balance Per Merchant"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchBalanceData}
            loading={loading}
          >
            Refresh
          </Button>
        }
      >
        <Table
          columns={balanceColumns}
          dataSource={members}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} merchant`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [topPaymentMethods, setTopPaymentMethods] = useState([]);
  const [topMerchants, setTopMerchants] = useState([]);
  const [netRevenueData, setNetRevenueData] = useState({ data: [], summary: null });
  const [merchants, setMerchants] = useState([]);
  const [filters, setFilters] = useState({
    period: 'daily',
    dateRange: null,
    paymentMethod: null,
    status: null,
    merchantId: null
  });

  useEffect(() => {
    fetchReportData();
  }, [filters]);

  useEffect(() => {
    adminApi.get('/master-products/approved-merchants')
      .then(r => setMerchants(r.data.data || []))
      .catch(() => {});
  }, []);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Fetch summary data
      const summaryParams = { period: filters.period };
      if (filters.dateRange) {
        summaryParams.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        summaryParams.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.merchantId) summaryParams.merchant_id = filters.merchantId;

      const summaryResponse = await adminApi.get('/reports/summary', { params: summaryParams });
      setSummary(summaryResponse.data);
      setTopPaymentMethods(summaryResponse.data.topPaymentMethods);
      setTopMerchants(summaryResponse.data.topMerchants);

      // Fetch revenue data
      const revenueParams = { period: filters.period };
      if (filters.dateRange) {
        revenueParams.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        revenueParams.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.merchantId) revenueParams.merchant_id = filters.merchantId;

      const revenueResponse = await adminApi.get('/reports/revenue', { params: revenueParams });
      setRevenueData(revenueResponse.data);

      // Fetch transaction data
      const transactionParams = { period: filters.period };
      if (filters.dateRange) {
        transactionParams.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        transactionParams.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.paymentMethod) transactionParams.payment_method = filters.paymentMethod;
      if (filters.status) transactionParams.status = filters.status;
      if (filters.merchantId) transactionParams.merchant_id = filters.merchantId;

      const transactionResponse = await adminApi.get('/reports/transactions', { params: transactionParams });
      setTransactionData(transactionResponse.data);

      // Fetch net revenue data
      const netRevenueParams = { period: filters.period };
      if (filters.dateRange) {
        netRevenueParams.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        netRevenueParams.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.merchantId) netRevenueParams.merchant_id = filters.merchantId;
      const netRevenueResponse = await adminApi.get('/reports/net-revenue', { params: netRevenueParams });
      setNetRevenueData(netRevenueResponse.data);

    } catch (error) {
      console.error('Fetch report data error:', error);

      // Use mock data as fallback (struktur harus sama dengan response API)
      const mockSummary = {
        summary: {
          total_transactions: 0,
          successful_transactions: 0,
          failed_transactions: 0,
          pending_transactions: 0,
          gross_revenue: 0,
          total_fees: 0,
          net_revenue: 0,
          avg_transaction_value: 0
        },
        topPaymentMethods: [],
        topMerchants: []
      };

      const mockRevenueData = [
        { date: '2024-11-12', revenue: 15200000, transactions: 45 },
        { date: '2024-11-13', revenue: 18750000, transactions: 52 },
        { date: '2024-11-14', revenue: 12300000, transactions: 38 },
        { date: '2024-11-15', revenue: 22100000, transactions: 61 },
        { date: '2024-11-16', revenue: 16800000, transactions: 44 },
        { date: '2024-11-17', revenue: 19200000, transactions: 58 },
        { date: '2024-11-18', revenue: 17400000, transactions: 47 }
      ];

      const mockTransactionData = [
        { transaction_id: 'TRX240001', merchant: 'Warung Bahagia', amount: 150000, method: 'QRIS', status: 'success', date: '2024-11-18T10:30:00Z' },
        { transaction_id: 'TRX240002', merchant: 'Toko Elektronik', amount: 75000, method: 'Bank Transfer', status: 'success', date: '2024-11-18T10:25:00Z' },
        { transaction_id: 'TRX240003', merchant: 'Cafe Sunset', amount: 200000, method: 'E-Wallet', status: 'success', date: '2024-11-18T10:20:00Z' }
      ];

      setSummary(mockSummary);
      setTopPaymentMethods(mockSummary.topPaymentMethods);
      setTopMerchants(mockSummary.topMerchants);
      setRevenueData(mockRevenueData);
      setTransactionData(mockTransactionData);

      console.log('Using fallback mock data for reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const params = { period: filters.period };

      if (filters.dateRange) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.paymentMethod) params.payment_method = filters.paymentMethod;
      if (filters.status) params.status = filters.status;
      if (filters.merchantId) params.merchant_id = filters.merchantId;

      // Create query string
      const queryString = new URLSearchParams(params).toString();

      // Trigger download
      const response = await adminApi.get(`/reports/export/csv?${queryString}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Data berhasil diekspor ke CSV');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Gagal mengekspor data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportNetRevenueExcel = async () => {
    try {
      setLoading(true);
      const params = { period: filters.period };

      if (filters.dateRange) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.merchantId) params.merchant_id = filters.merchantId;

      const queryString = new URLSearchParams(params).toString();

      const response = await adminApi.get(`/reports/export/net-revenue-excel?${queryString}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laporan_pendapatan_bersih_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Laporan Pendapatan Bersih berhasil diekspor ke Excel');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Gagal mengekspor laporan');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const transactionColumns = [
    {
      title: 'ID Transaksi',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
    },
    {
      title: 'Merchant',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 200,
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 150,
      render: (amount) => (
        <strong>{formatCurrency(amount)}</strong>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
    },
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm:ss'),
    },
  ];

  const topPaymentMethodsColumns = [
    {
      title: 'Metode Pembayaran',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Jumlah Transaksi',
      dataIndex: 'transaction_count',
      key: 'transaction_count',
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => formatCurrency(amount),
    },
  ];

  const topMerchantsColumns = [
    {
      title: 'Merchant',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
    },
    {
      title: 'Jumlah Transaksi',
      dataIndex: 'transaction_count',
      key: 'transaction_count',
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => formatCurrency(amount),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Laporan Arendo</h1>
        <p style={{ color: '#666' }}>Analisis dan laporan transaksi Arendo</p>
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              placeholder={['Dari tanggal', 'Sampai tanggal']}
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Periode"
              value={filters.period}
              onChange={(value) => setFilters(prev => ({ ...prev, period: value }))}
              style={{ width: '100%' }}
            >
              <Option value="daily">Harian</Option>
              <Option value="weekly">Mingguan</Option>
              <Option value="monthly">Bulanan</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              showSearch
              placeholder="Filter Mitra"
              value={filters.merchantId}
              onChange={(value) => setFilters(prev => ({ ...prev, merchantId: value }))}
              style={{ width: '100%' }}
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {merchants.map(m => (
                <Option key={m.id} value={m.id}>{m.full_name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              loading={loading}
            >
              Export CSV
            </Button>
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchReportData}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Transaksi"
                value={summary?.summary?.total_transactions ?? 0}
                prefix={<TransactionOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Transaksi Berhasil"
                value={summary?.summary?.successful_transactions ?? 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Transaksi Gagal"
                value={summary?.summary?.failed_transactions ?? 0}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Volume Transaksi"
                value={summary?.summary?.volume_transaksi ?? 0}
                prefix={<DollarOutlined />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
              <Statistic
                title="Total Fee"
                value={summary?.summary?.total_fee ?? 0}
                prefix={<DollarOutlined />}
                formatter={(value) => formatCurrency(value)}
                valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs defaultActiveKey="revenue">
        <TabPane tab="Grafik Pendapatan" key="revenue">
          <Card title="Tren Pendapatan">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value, name) => [
                  name.includes('amount') ? formatCurrency(value) : value,
                  name === 'transaction_count' ? 'Jumlah Transaksi' :
                  name === 'gross_amount' ? 'Pendapatan Kotor' :
                  name === 'net_amount' ? 'Pendapatan Bersih' : name
                ]} />
                <Bar dataKey="gross_amount" fill="#8884d8" name="Pendapatan Kotor" />
                <Bar dataKey="net_amount" fill="#82ca9d" name="Pendapatan Bersih" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabPane>

        <TabPane tab="Metode Pembayaran" key="paymentMethods">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Distribusi Metode Pembayaran">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={topPaymentMethods}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="transaction_count"
                      label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    >
                      {topPaymentMethods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Top Metode Pembayaran">
                <Table
                  columns={topPaymentMethodsColumns}
                  dataSource={topPaymentMethods}
                  rowKey="name"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Top Merchant" key="merchants">
          <Card title="Merchant dengan Transaksi Terbanyak">
            <Table
              columns={topMerchantsColumns}
              dataSource={topMerchants}
              rowKey="merchant_name"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Detail Transaksi" key="transactions">
          <Card title="Detail Transaksi">
            <Table
              columns={transactionColumns}
              dataSource={transactionData}
              rowKey="transaction_id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} dari ${total} transaksi`,
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Pendapatan Bersih" key="netRevenue">
          <Card
            title="Laporan Pendapatan Bersih"
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportNetRevenueExcel}
                loading={loading}
              >
                Export Excel
              </Button>
            }
          >
            {/* Net Revenue Summary */}
            {netRevenueData.summary && (
              <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" style={{ background: '#f0f5ff' }}>
                    <Statistic
                      title="Total Transaksi Berhasil"
                      value={netRevenueData.summary.total_transactions || 0}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" style={{ background: '#fff7e6' }}>
                    <Statistic
                      title="Volume Transaksi"
                      value={netRevenueData.summary.total_volume || 0}
                      formatter={(value) => formatCurrency(value)}
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card size="small" style={{ background: '#f6ffed' }}>
                    <Statistic
                      title="Total Fee"
                      value={netRevenueData.summary.total_fee || 0}
                      formatter={(value) => formatCurrency(value)}
                      valueStyle={{ color: '#52c41a', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* Net Revenue Chart */}
            <div style={{ marginBottom: '24px' }}>
              <Text strong>Grafik Pendapatan Bersih per Periode</Text>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={netRevenueData.data || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    formatCurrency(value),
                    name === 'volume_transaksi' ? 'Volume Transaksi' :
                    name === 'total_fee' ? 'Total Fee' : name
                  ]} />
                  <Bar dataKey="volume_transaksi" fill="#1890ff" name="Volume Transaksi" />
                  <Bar dataKey="total_fee" fill="#52c41a" name="Total Fee" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Net Revenue Table */}
            <Table
              columns={[
                {
                  title: 'Periode',
                  dataIndex: 'period',
                  key: 'period',
                },
                {
                  title: 'Jumlah Transaksi',
                  dataIndex: 'transaction_count',
                  key: 'transaction_count',
                  align: 'right',
                },
                {
                  title: 'Volume Transaksi',
                  dataIndex: 'volume_transaksi',
                  key: 'volume_transaksi',
                  align: 'right',
                  render: (value) => formatCurrency(parseFloat(value) || 0),
                },
                {
                  title: 'Total Fee',
                  dataIndex: 'total_fee',
                  key: 'total_fee',
                  align: 'right',
                  render: (value) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{formatCurrency(parseFloat(value) || 0)}</span>,
                },
              ]}
              dataSource={netRevenueData.data || []}
              rowKey="period"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} periode`,
              }}
              summary={() => netRevenueData.summary && (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                    <Table.Summary.Cell>TOTAL</Table.Summary.Cell>
                    <Table.Summary.Cell align="right">{netRevenueData.summary.total_transactions || 0}</Table.Summary.Cell>
                    <Table.Summary.Cell align="right">{formatCurrency(parseFloat(netRevenueData.summary.total_volume) || 0)}</Table.Summary.Cell>
                    <Table.Summary.Cell align="right" style={{ color: '#52c41a' }}>{formatCurrency(parseFloat(netRevenueData.summary.total_fee) || 0)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>
        </TabPane>

        <TabPane tab="Balance Reports" key="balance">
          <BalanceReports loading={loading} />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Reports;